/**
 * 사진첩에서 고른 사진을 담을 수 있는 크기로 줄입니다 — 화면 F2-2.
 *
 * 설계는 이 자리를 «네이티브 사진 선택 브리지»로 잡아 두었지만, 웹앱으로
 * 홀로 서기로 하면서 브리지가 필요 없어졌습니다. 브라우저의 파일 선택이
 * 곧 사진첩입니다 — iOS 사파리에서 `accept="image/*"` 는 사진 보관함 ·
 * 사진 찍기 · 파일 선택을 함께 띄웁니다. 권한 창(F2-2)도 OS 가 알아서
 * 띄우므로 우리가 그릴 화면이 없습니다.
 *
 * **원본을 그대로 보내지 않습니다.** 요즘 휴대폰 사진은 한 장에 3~5MB 이고,
 * 모임 사진이 쓰이는 가장 큰 자리가 88px 짜리 칸입니다. 한 변 256px 로
 * 줄이면 20KB 안팎이라 컬럼에 담아도 부담이 없습니다.
 *
 * 정사각으로 **잘라서** 담습니다. 목록의 얼굴도 미리 보기도 정사각이라,
 * 줄이기만 하면 화면마다 다른 곳이 잘려 보입니다.
 */

/** 한 변. GroupPhoto 가 그려지는 가장 큰 자리(미리 보기)보다 넉넉합니다 */
const SIZE = 256;

/** JPEG 품질. 0.8 이면 눈에 띄는 손상 없이 크기가 크게 줍니다 */
const QUALITY = 0.8;

export class ImageError extends Error {}

/**
 * 고른 파일을 정사각 JPEG 데이터 URL 로 바꿉니다.
 *
 * 실패하는 길이 여럿이라(그림이 아닌 파일, 깨진 파일, 메모리 부족) 전부
 * ImageError 로 모읍니다 — 부르는 쪽은 사람에게 보여 줄 문장 하나만 받습니다.
 */
export async function toSquareDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new ImageError('그림 파일이 아닙니다.');
  }

  const { image, release } = await load(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ImageError('사진을 줄이지 못했습니다.');

    // 짧은 변을 기준으로 가운데를 정사각으로 잘라 냅니다
    const side = Math.min(image.width, image.height);
    const sx = (image.width - side) / 2;
    const sy = (image.height - side) / 2;
    ctx.drawImage(image, sx, sy, side, side, 0, 0, SIZE, SIZE);

    // 투명이 있는 그림은 JPEG 에서 검게 나옵니다. 흰 바탕을 먼저 깔 수도
    // 있지만, 잘라 채우는 그림이라 투명이 남는 경우가 사실상 없습니다.
    return canvas.toDataURL('image/jpeg', QUALITY);
  } finally {
    release();
  }
}

interface Loaded {
  image: ImageBitmap | HTMLImageElement;
  /** **그린 뒤에** 부릅니다. 먼저 풀면 그릴 것이 사라진 판이 있습니다 */
  release: () => void;
}

/**
 * 파일에서 그림을 읽습니다.
 *
 * createImageBitmap 이 빠르고 메모리도 덜 쓰지만 사파리의 오래된 판에는
 * 없습니다. 없으면 <img> 로 떨어집니다 — 둘 다 drawImage 가 받습니다.
 */
async function load(file: File): Promise<Loaded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      // 직접 닫아 줘야 메모리가 돌아옵니다
      return { image: bitmap, release: () => bitmap.close() };
    } catch {
      // 사파리가 일부 형식(HEIC 등)에서 실패합니다. <img> 가 더 너그럽습니다
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new ImageError('사진을 읽지 못했습니다.'));
      el.src = url;
    });
    return { image: img, release: () => URL.revokeObjectURL(url) };
  } catch (e) {
    // 여기서 실패하면 그릴 일이 없으므로 바로 풉니다
    URL.revokeObjectURL(url);
    throw e;
  }
}
